/**
 * Date Utilities
 *
 * Common date manipulation and formatting functions
 */
import { format, parseISO, isValid, addDays, addMonths, addYears, startOfDay, endOfDay, startOfMonth, endOfMonth, differenceInDays, differenceInHours, differenceInMinutes, formatDistanceToNow, } from 'date-fns';
/**
 * Format date to ISO string
 */
export const toISOString = (date) => {
    return date.toISOString();
};
/**
 * Parse ISO string to date
 */
export const fromISOString = (iso) => {
    return parseISO(iso);
};
/**
 * Check if date is valid
 */
export const isValidDate = (date) => {
    return date instanceof Date && isValid(date);
};
/**
 * Format date with pattern
 * Handles both Date objects and ISO strings
 * Returns '-' if date is falsy
 */
export const formatDate = (date, pattern = 'yyyy-MM-dd') => {
    if (!date)
        return '-';
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    return format(dateObj, pattern);
};
/**
 * Format datetime
 * Handles both Date objects and ISO strings
 * Returns '-' if date is falsy
 */
export const formatDateTime = (date) => {
    if (!date)
        return '-';
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    return format(dateObj, 'yyyy-MM-dd HH:mm:ss');
};
/**
 * Format time only
 */
export const formatTime = (date) => {
    return format(date, 'HH:mm:ss');
};
/**
 * Add days to date
 */
export const addDaysToDate = (date, days) => {
    return addDays(date, days);
};
/**
 * Add months to date
 */
export const addMonthsToDate = (date, months) => {
    return addMonths(date, months);
};
/**
 * Add years to date
 */
export const addYearsToDate = (date, years) => {
    return addYears(date, years);
};
/**
 * Get start of day
 */
export const getStartOfDay = (date) => {
    return startOfDay(date);
};
/**
 * Get end of day
 */
export const getEndOfDay = (date) => {
    return endOfDay(date);
};
/**
 * Get start of month
 */
export const getStartOfMonth = (date) => {
    return startOfMonth(date);
};
/**
 * Get end of month
 */
export const getEndOfMonth = (date) => {
    return endOfMonth(date);
};
/**
 * Calculate days between dates
 */
export const getDaysBetween = (start, end) => {
    return differenceInDays(end, start);
};
/**
 * Calculate hours between dates
 */
export const getHoursBetween = (start, end) => {
    return differenceInHours(end, start);
};
/**
 * Calculate minutes between dates
 */
export const getMinutesBetween = (start, end) => {
    return differenceInMinutes(end, start);
};
/**
 * Check if date is in past
 */
export const isInPast = (date) => {
    return date < new Date();
};
/**
 * Check if date is in future
 */
export const isInFuture = (date) => {
    return date > new Date();
};
/**
 * Check if date is today
 */
export const isToday = (date) => {
    const today = new Date();
    return (date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear());
};
/**
 * Get current timestamp
 */
export const now = () => {
    return new Date();
};
/**
 * Get UTC timestamp
 */
export const utcNow = () => {
    const now = new Date();
    return new Date(now.getTime() + now.getTimezoneOffset() * 60000);
};
/**
 * Create date range
 */
export const createDateRange = (start, end) => {
    return { from: start, to: end };
};
/**
 * Check if date is in range
 */
export const isInRange = (date, start, end) => {
    return date >= start && date <= end;
};
/**
 * Format relative time (e.g., "2 hours ago", "in 3 days")
 * Handles both Date objects and ISO strings
 */
export const formatRelativeTime = (date) => {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    return formatDistanceToNow(dateObj, { addSuffix: true });
};
